import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useRef } from 'react';
import type { TitleAndTextProps } from './TitleAndText.data';
import styles from './TitleAndText.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './TitleAndText.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import Heading from '../../atoms/heading/Heading';
import { NODE_HEADING, NODE_PARAGRAPH, render } from 'storyblok-rich-text-react-renderer';
import Text from '../../atoms/text/Text';
import type { HeadingAs, HeadingSize } from '../../atoms/heading/Heading.data';
import type { TextSize } from '../../atoms/text/Text.data';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';

export default function TitleAndText({
  isVisible,
  className,
  blok,
  ref,
}: TitleAndTextProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);
  useThemeScrollBlock(elementRef, blok.use_accent_color);

  const mapHeading = (level: number): string => `heading-0${level}`;

  const mapDescription = (titleLevel: number): string => {
    if (titleLevel > 2) {
      return 'body-standard';
    }
    return 'body-large';
  };

  const titleLevel = useRef<number>(3);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.titleAndText, className, 'grid-gutter')}
      {...sbEditable(blok)}
    >
      {render(blok.title, {
        nodeResolvers: {
          [NODE_HEADING]: (children, { level }) => {
            titleLevel.current = level;

            return (
              <Heading
                as={`h${level}` as HeadingAs}
                className={classNames(styles[mapHeading(level)], styles.title)}
                size={mapHeading(level) as HeadingSize}
                disableTriggerInOnScroll={false}
                enableChineseTransitionFallback
              >
                {children}
              </Heading>
            );
          },
        },
      })}

      {blok.text &&
        blok.text.content[0] &&
        render(blok.text, {
          nodeResolvers: {
            [NODE_PARAGRAPH]: (children) => {
              if (children) {
                return (
                  <Text
                    className={classNames(styles.description)}
                    as="p"
                    size={mapDescription(titleLevel.current) as TextSize}
                    transitionInType="splitCharsWordsRichText"
                    disableTriggerInOnScroll={false}
                  >
                    {children}
                  </Text>
                );
              }
              return null;
            },
          },
        })}
    </div>
  );
}
